// Global Instructions Rule Applied!
//
// lineDiff — a small line-level diff (longest common subsequence) for the
// minutes History modal: which lines a version added or removed relative to
// the one before it. Documents are a few hundred lines at most, so the
// O(n·m) table is fine; no dependency needed.

export type LineDiffKind = 'same' | 'add' | 'del'

export interface LineDiffOp {
  kind: LineDiffKind
  text: string
}

// Trailing blank lines are not a change worth showing (an agent doc keeps
// them, a person's edit is trimmed on save).
const splitLines = (text: string): string[] => {
  const normalised = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '')
  return normalised === '' ? [] : normalised.split('\n')
}

/** Ordered diff ops from `before` to `after`: unchanged lines as 'same',
 *  lines only in `after` as 'add', lines only in `before` as 'del'. */
export const diffLines = (before: string, after: string): LineDiffOp[] => {
  const a = splitLines(before)
  const b = splitLines(after)
  const n = a.length
  const m = b.length
  // lcs[i][j] = LCS length of a[i..] and b[j..]
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0))
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }
  const ops: LineDiffOp[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ kind: 'same', text: a[i] })
      i += 1
      j += 1
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      ops.push({ kind: 'del', text: a[i] })
      i += 1
    } else {
      ops.push({ kind: 'add', text: b[j] })
      j += 1
    }
  }
  while (i < n) {
    ops.push({ kind: 'del', text: a[i] })
    i += 1
  }
  while (j < m) {
    ops.push({ kind: 'add', text: b[j] })
    j += 1
  }
  return ops
}

/** Counts of added / removed lines — the summary line above a diff. */
export const diffSummary = (ops: LineDiffOp[]): { added: number; removed: number } => ({
  added: ops.filter((op) => op.kind === 'add').length,
  removed: ops.filter((op) => op.kind === 'del').length
})
